import { Link } from "react-router-dom";
import { ArrowLeft, Shield, FileText, AlertTriangle, Scale } from "lucide-react";
import { BRAND, LOGO_URL } from "../common/constants";
import Footer from "../common/Footer";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BRAND.parent} className="h-10 w-auto rounded-md" />
            <span className="font-heading font-bold text-[#071734]">{BRAND.parent}</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#00a3e0]">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="container-main max-w-4xl">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#071734] rounded-lg flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-[#071734]">Terms of Use</h1>
                <p className="text-gray-500">Last Updated: August 26, 2026</p>
              </div>
            </div>
          </div>

          <div className="prose prose-gray max-w-none space-y-8">
            
            {/* Introduction */}
            <section className="p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h2 className="text-xl font-semibold text-[#071734] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#00a3e0]" /> Agreement to Terms
              </h2>
              <p className="text-gray-700">
                By accessing or using {BRAND.parent} services, including our Resume Optimizer and related AI-powered tools 
                (collectively, the "Services"), you agree to be bound by these Terms of Use. If you do not agree to these 
                terms, please do not use our Services.
              </p>
            </section>

            {/* AI Disclaimer - Most Important */}
            <section className="p-6 bg-amber-50 rounded-lg border border-amber-300">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> AI-Generated Content Disclaimer
              </h2>
              <div className="space-y-4 text-amber-900">
                <p>
                  <strong>IMPORTANT:</strong> Our Services utilize artificial intelligence (AI) technology to generate content, 
                  including but not limited to resumes, cover letters, CVs, and interview preparation guides.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>AI Limitations:</strong> AI systems can and do make mistakes. Generated content may contain errors, 
                    inaccuracies, or inappropriate suggestions. AI cannot verify the accuracy of information you provide.
                  </li>
                  <li>
                    <strong>User Responsibility:</strong> You are solely responsible for reviewing, verifying, and editing all 
                    AI-generated content before use. We strongly recommend thorough review of all documents.
                  </li>
                  <li>
                    <strong>No Guarantees:</strong> We do not guarantee that AI-generated content will be error-free, suitable 
                    for any particular purpose, or result in any specific outcome (such as job interviews or employment).
                  </li>
                  <li>
                    <strong>Ethical AI:</strong> Our AI is designed to only use information you provide and will not fabricate 
                    credentials, degrees, or work experience. However, you must verify all content for accuracy.
                  </li>
                </ul>
              </div>
            </section>

            {/* Data & Storage */}
            <section className="p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h2 className="text-xl font-semibold text-[#071734] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#00a3e0]" /> Data Storage & Session Information
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  <strong>Temporary Storage:</strong> Resume content, personal information submitted for document generation,
                  and generated documents may be temporarily stored for up to approximately 24 hours to support processing and
                  downloads. This temporary data is scheduled for automatic deletion after the retention period.
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Download Promptly:</strong> Download and save your generated documents as soon as possible.
                    Temporary server copies are not intended for long-term storage.
                  </li>
                  <li>
                    <strong>Temporary Availability:</strong> Generated documents are intended to remain available only temporarily
                    and may no longer be recoverable after the retention period expires.
                  </li>
                  <li>
                    <strong>No Account Recovery:</strong> Without user accounts, we cannot recover lost work. Please save your 
                    documents locally.
                  </li>
                </ul>
              </div>
            </section>

            {/* Use of Services */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Acceptable Use</h2>
              <p className="text-gray-700 mb-4">You agree to use our Services only for lawful purposes. You may not:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide false, misleading, or fraudulent information</li>
                <li>Use generated content to misrepresent your qualifications or experience</li>
                <li>Attempt to circumvent any security features or access restrictions</li>
                <li>Use the Services in any way that could damage, disable, or impair the platform</li>
                <li>Reproduce, distribute, or resell the Services or generated content for commercial purposes</li>
              </ul>
            </section>

            {/* Payment Terms */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Payment & Refunds</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Payment is required before document generation. All sales are final once document generation begins. 
                  However, if you experience technical issues that prevent you from receiving your documents:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Contact us within 24 hours of purchase at <a href="mailto:aaron@aiquires.com" className="text-[#00a3e0] hover:underline">aaron@aiquires.com</a></li>
                  <li>We will work to resolve the issue or provide a refund at our discretion</li>
                  <li>Refunds are not available for dissatisfaction with AI-generated content quality</li>
                </ul>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="p-6 bg-gray-100 rounded-lg">
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Limitation of Liability</h2>
              <div className="space-y-4 text-gray-700 text-sm">
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, {BRAND.parent.toUpperCase()}, ITS AFFILIATES, AND THEIR RESPECTIVE 
                  OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
                  CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Loss of employment opportunities</li>
                  <li>Damages resulting from errors in AI-generated content</li>
                  <li>Loss of data or documents</li>
                  <li>Any other damages arising from your use of or inability to use the Services</li>
                </ul>
                <p className="mt-4">
                  OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SPECIFIC SERVICE GIVING RISE TO THE CLAIM.
                </p>
              </div>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify and hold harmless {BRAND.parent}, its affiliates, and their respective officers, 
                directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable 
                attorney fees) arising from your use of the Services, your violation of these Terms, or your violation 
                of any rights of another party.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. 
                Your continued use of the Services after any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            {/* Contact */}
            <section className="p-6 bg-[#00a3e0]/10 rounded-lg border border-[#00a3e0]/30">
              <h2 className="text-xl font-semibold text-[#071734] mb-4">Contact Us</h2>
              <p className="text-gray-700">
                If you have questions about these Terms or need support, please contact us at:
              </p>
              <p className="mt-2">
                <a href="mailto:aaron@aiquires.com" className="text-[#00a3e0] hover:underline font-medium">
                  aaron@aiquires.com
                </a>
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer variant="simple" />
    </div>
  );
};

export default TermsPage;
